import {
  faCircleCheck,
  faCircleInfo,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import { cn } from "../ClassName";

type WarningType =
  | "error"
  | "warning"
  | "success"
  | "gray-info"
  | "blue-info"
  | "blue-success"
  | "red-info"
  | "mauve-info";

type IconSize = "2xs" | "xs" | "sm" | "lg" | "xl" | "2xl";

/**
 * Used to display inline warning/error messages with the colored border
 * (if not error) can be hidden through the ignore button
 * @param warningType changes the icon and text color, cannot be hidden
 * @param hidden whether or not the message is hidden
 * @param setHidden function to set hidden attribute
 * @param closeMessage optional message to be displayed on the close button
 */

const GenericWarningMessageWithBorder = (props: {
  className?: string;
  warningType: WarningType;
  children?: React.ReactNode;
  hidden?: boolean;
  closeMessage?: string;
  iconSize?: IconSize;
  setHidden?: () => void;
  isIconCentered?: boolean;
}) => {
  const config: Record<
    WarningType,
    { icon: IconProp; iconClassName: string; className: string }
  > = {
    error: {
      icon: faTriangleExclamation,
      iconClassName: "tw-text-red-1",
      className: "tw-border-red-1 tw-bg-red-light-1 tw-text-red-1",
    },
    warning: {
      icon: faTriangleExclamation,
      iconClassName: "tw-text-orange-1",
      className: "tw-border-orange-1 tw-bg-orange-light-1 tw-text-orange-1",
    },
    success: {
      icon: faCircleCheck,
      iconClassName: "tw-text-green-1",
      className: "tw-border-green-1 tw-bg-green-light-1 tw-text-green-1",
    },
    "gray-info": {
      icon: faCircleInfo,
      iconClassName: "tw-text-gray-3",
      className: "tw-border-gray-light-1 tw-bg-gray-light-3 tw-text-gray-3",
    },
    "blue-info": {
      icon: faCircleInfo,
      iconClassName: "tw-text-blue-1",
      className: "tw-border-blue-1 tw-bg-blue-light-1 tw-text-blue-1",
    },
    "blue-success": {
      icon: faCircleCheck,
      iconClassName: "tw-text-blue-1",
      className: "tw-border-blue-1 tw-bg-blue-light-1 tw-text-blue-1",
    },
    "red-info": {
      icon: faCircleInfo,
      iconClassName: "tw-text-red-1",
      className: "tw-border-red-1 tw-bg-red-light-1 tw-text-red-1",
    },
    "mauve-info": {
      icon: faTriangleExclamation,
      iconClassName: "tw-text-mauve-1",
      className: "tw-border-mauve-1 tw-bg-mauve-light-1 tw-text-mauve-1",
    },
  };

  const iconSizes: Record<IconSize, number> = {
    "2xs": 10,
    xs: 12,
    sm: 14,
    lg: 20,
    xl: 24,
    "2xl": 32,
  };

  const { icon, iconClassName, className } = config[props.warningType];

  return (
    <div
      className={cn(
        "tw-align-start tw-flex tw-w-full tw-rounded-lg tw-border tw-border-solid tw-px-[16px] tw-py-[8px]",
        props.warningType === "gray-info" || props.isIconCentered
          ? "tw-border-l-[1px]"
          : "tw-border-l-[4px]",
        props.hidden ? "tw-hidden" : "",
        className,
        props.className,
      )}
    >
      <div
        className={cn(
          "tw-flex tw-flex-col tw-place-content-center",
          !props.isIconCentered ? "tw-h-[22px]" : "",
        )}
      >
        <FontAwesomeIcon
          icon={icon}
          className={cn(
            `tw-mr-[8px] tw-text-[${props.iconSize ? iconSizes[props.iconSize] : 16}px]`,
            iconClassName,
          )}
        />
      </div>
      <span className="tw-inline-block tw-w-full">
        {props.children}
        {!!props.closeMessage && (
          <button
            className="tw-text-blue-500 tw-border-none tw-bg-transparent tw-underline"
            onClick={props.setHidden || undefined}
          >
            {props.closeMessage ?? "Ignore"}
          </button>
        )}
      </span>
    </div>
  );
};

export default GenericWarningMessageWithBorder;
